// ---------------------------------------------------------------------------
// HBV model (EvalHid variant, no integrated snow routine — snow is modeled
// separately, see snow1.ts — nor triangular-hydrograph routing MAXBAS):
// non-linear soil routine (Beta/FC/Pwp) + two cascaded response cells
// (subsurface S1 with K0/K1/Kperc, deep S2 with K2).
//
// Formulation confirmed by decompiling EvalHid's real calculation engine
// (Evalhid.dll, .NET 6, via ilspycmd — see class HBV.calculo_hbv()), not
// just against the Technical Manual: there's one real structural
// difference —
//
// The soil moisture surplus (when H exceeds FC) does NOT leave as an
// independent direct runoff term: it's added to infiltration `t` and enters
// through the same path as the rest towards S1 (via K0/K1/Kperc), unlike
// what a literal reading of the Manual (Annex A.2) suggests, which implies
// a separate Qs term.
//
// When S1's balance (input − Q0 − Q1 − Qperc) goes negative, the .exe
// clamps in cascade — first Q0, updates S1, then Q1 with the already-clamped
// S1, then Qperc — instead of a single global clamp; that exact order is
// replicated.
//
// The deep cell (S2/Q2) is ALWAYS calculated, whether or not the sub-basin
// has a real aquifer assigned — but `contribution` only includes Q2 when
// `hasAquifer` is false. Historically the .exe checked `cod_acuifero ==
// "-1"`, a sentinel that never appears in real data (the real CSV writes
// "0" for "no aquifer"), making the check dead code and Q2 always counted
// — confirmed fixed in the real source (2026-08, see CLAUDE.md's
// engine-source audit): `Hbv.cs` now checks `cod_acuifero == "0"` and, with
// a real aquifer assigned, excludes Q2 (base flow) from the sub-basin's own
// contribution (assumed to flow to the aquifer's own aggregate balance
// instead — see aggregation.ts).
//
// Pumping (`bomb`, mm/step): subtracted from the deep cell's own inflow,
// before Q2 is drawn from it (`Hbv.cs`: `slo = Max(0, slo + infilt -
// bomb)`). Resolved from the sub-basin's own `Ent_Bomb.csv` column and
// converted to mm/step before reaching here — see temez.ts's own header
// for the full resolution/conversion chain, shared by all 6 models.
// `null` only when the sub-basin has no pumping column of its own, or
// "Modelar bombeos" is unchecked.
//
// Naming trap, found comparing Sal_parciales.csv against this port's own
// detail export: `Hbv.cs`'s local variable `infilt` (what Sal_parciales.csv
// calls "Infilt") is the percolation from S1 into S2 — `detail.percolation`
// below, bit-for-bit identical to the real .exe's own "Infilt" column,
// verified against a real Bellús run including pumping (2026-08-26). It is
// NOT the same quantity as the soil routine's own infiltration into S1
// (`detail.soilInfiltration`), which the real .exe never exports as its own
// column at all — comparing that field against Sal_parciales.csv's "Infilt"
// column will always look wrong, because they're different quantities that
// happen to share a name.
// ---------------------------------------------------------------------------

export interface HbvParameters {
  /** Beta (dimensionless): non-linearity of the soil infiltration routine. */
  beta: number;
  /** FC (mm): soil field capacity. */
  fc: number;
  /** Pwp (mm): threshold below which actual ET is linearly reduced. */
  pwp: number;
  /** Lmax (mm): trigger threshold for fast runoff in the subsurface cell. */
  lmax: number;
  /** K0 (1/step): fast runoff constant (S1 above Lmax). */
  k0: number;
  /** K1 (1/step): interflow constant (S1). */
  k1: number;
  /** K2 (1/step): base flow constant (S2). */
  k2: number;
  /** Kperc (1/step): percolation constant from S1 to S2. */
  kperc: number;
}

interface HbvState {
  /** Ho (mm): initial soil moisture. */
  ho: number;
  /** S1 (mm): initial subsurface cell storage. */
  s1: number;
  /** S2 (mm): initial deep cell storage. */
  s2: number;
}

interface HbvDetail {
  [key: string]: Float64Array;
  /** Soil moisture after the step. */
  soilMoisture: Float64Array;
  /** Actual evapotranspiration. */
  etr: Float64Array;
  /** Infiltration into S1 (includes the soil moisture surplus, see header). Not exported by the real .exe on its own — it has no matching column in Sal_parciales.csv. */
  soilInfiltration: Float64Array;
  /** S1 (mm): subsurface cell after the step. */
  s1: Float64Array;
  /** S2 (mm): deep cell after the step. */
  s2: Float64Array;
  /** Q0 (mm): fast runoff. */
  q0: Float64Array;
  /** Q1 (mm): interflow. */
  q1: Float64Array;
  /** Q2 (mm): base flow. */
  q2: Float64Array;
  /** Percolation from S1 to S2 — this is what the real .exe calls "Infilt" in Sal_parciales.csv, a different quantity from soilInfiltration above (see header). */
  percolation: Float64Array;
}

interface HbvResult {
  /** Total contribution (Q0 + Q1 + Q2) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: HbvDetail;
}

/**
 * Simulates the HBV model (EvalHid variant) for a single sub-basin.
 * `precipitation` and `etp` must already come adjusted (precipAdjustment/etpAdjustment
 * applied) and aligned step by step.
 */
export function calculateHbv(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: HbvParameters,
  initialState: HbvState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): HbvResult {
  const { beta, fc, pwp, lmax, k0, k1, k2, kperc } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: HbvDetail = {
    soilMoisture: new Float64Array(n),
    etr: new Float64Array(n),
    soilInfiltration: new Float64Array(n),
    s1: new Float64Array(n),
    s2: new Float64Array(n),
    q0: new Float64Array(n),
    q1: new Float64Array(n),
    q2: new Float64Array(n),
    percolation: new Float64Array(n),
  };

  let sm = initialState.ho;
  let s1 = initialState.s1;
  let s2 = initialState.s2;

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const potentialEtp = Number.isFinite(etp[t]) ? etp[t] : 0;

    // Infiltration into S1, bounded to [0, p].
    let infiltration = (sm / fc) ** beta * p;
    if (infiltration > p) infiltration = p;
    if (infiltration < 0) infiltration = 0;

    // Actual ET.
    const etr = Math.max(0, sm < pwp ? potentialEtp * (sm / pwp) : potentialEtp);

    // Soil moisture; if it saturates, the surplus is added to infiltration (no separate direct runoff).
    let nextSm = Math.max(0, sm + p - infiltration - etr);
    if (nextSm > fc) {
      infiltration += nextSm - fc;
      nextSm = fc;
    }

    // Subsurface cell: Q0 (fast, above Lmax), Q1 (interflow), Qperc (towards S2).
    let q0 = Math.max(0, (s1 - lmax) * k0);
    let q1 = Math.max(0, s1 * k1);
    let qperc = Math.max(0, s1 * kperc);
    const balance = s1 + infiltration - q0 - q1 - qperc;
    let nextS1: number;
    if (balance < 0) {
      // Cascading clamp: first Q0 (with S1 bounded to what's available), then Q1, then Qperc.
      q0 = Math.min(s1, Math.max(0, (s1 - lmax) * k0));
      let partialS1 = Math.min(s1, Math.max(0, s1 + infiltration - q0));
      q1 = Math.min(partialS1, Math.max(0, partialS1 * k1));
      partialS1 = Math.min(partialS1, Math.max(0, partialS1 - q1));
      qperc = Math.min(partialS1, Math.max(0, partialS1 * kperc));
      nextS1 = Math.max(0, partialS1 - qperc);
    } else {
      nextS1 = balance;
    }

    // Deep cell: base flow. Always calculated (see header). Percolation
    // (qperc, "infilt" in the real source) and pumping are applied to S2
    // BEFORE Q2 is drawn from it — not after, as an earlier version of this
    // port did (computing Q2 from the previous step's S2 instead of this
    // step's own updated one). Confirmed against Hbv.cs (2026-08, see
    // CLAUDE.md's engine-source audit).
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;
    const s2WithInflow = Math.max(0, s2 + qperc - bomb);
    const q2 = Math.max(0, s2WithInflow * k2);
    const nextS2 = Math.max(0, s2WithInflow - q2);

    contribution[t] = hasAquifer ? q0 + q1 : q0 + q1 + q2;

    sm = nextSm;
    s1 = nextS1;
    s2 = nextS2;

    detail.soilMoisture[t] = sm;
    detail.etr[t] = etr;
    detail.soilInfiltration[t] = infiltration;
    detail.s1[t] = s1;
    detail.s2[t] = s2;
    detail.q0[t] = q0;
    detail.q1[t] = q1;
    detail.q2[t] = q2;
    detail.percolation[t] = qperc;
  }

  return { contribution, detail };
}
